import {useSortable} from '@dnd-kit/sortable';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import React, {useCallback, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {trackPanelBankEvent} from '../../services/analytics';
import {useDispatch} from '../../state/hooks';
import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import * as PanelBankSectionConfigActions from '../../state/views/panelBankSectionConfig/actions';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {SectionSettings} from '../SettingControls/SectionSettings';
import {usePanelBankSectionIsOpen} from './hooks/panelBankSectionIsOpenHooks';
import {PanelBankSectionOverflowMenu} from './PanelBankSectionOverflowMenu';
import {PanelBankSectionPagination} from './PanelBankSectionPagination';
import {PanelBankSectionTitle} from './PanelBankSectionTitle';
import {getDndSortableSectionId} from './utils';

export const PanelBankSectionHeader = ({
  activePanelCount,
  isPinned,
  isProjectReadOnly,
  isSectionNameSearchMatch,
  panelBankSectionConfigRef,
  isHiddenPanelsSection,
  panelSearchMatchCount = 0,
  pinPanelBankSection,
  sectionComponentRef,
  sectionLayoutType,
  sectionName,
}: {
  activePanelCount: number;
  isPinned: boolean;
  isProjectReadOnly?: boolean;
  isSectionNameSearchMatch?: boolean;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  isHiddenPanelsSection: boolean;
  panelSearchMatchCount?: number;
  pinPanelBankSection?: (sectionId: PanelBankSectionConfigTypes.Ref) => void;
  sectionComponentRef: React.RefObject<HTMLDivElement>;
  /**
   * @deprecated We are moving towards one layout type. We need to remove this once migration is complete.
   */
  sectionLayoutType: 'flow' | 'grid';
  sectionName: string;
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const {setActivatorNodeRef, listeners, attributes} = useSortable({
    id: getDndSortableSectionId(panelBankSectionConfigRef),
  });

  const {sectionIsOpen, toggleSectionIsOpen} = usePanelBankSectionIsOpen({
    panelBankSectionConfigRef,
  });
  const {isSearching} = usePanelSearchDebouncedContext();

  const dispatch = useDispatch();
  const updateName = useCallback(
    (newName: string) => {
      dispatch(
        PanelBankSectionConfigActions.updateName(
          panelBankSectionConfigRef,
          newName
        )
      );
    },
    [dispatch, panelBankSectionConfigRef]
  );

  const onRemovePinnedSection = useCallback(
    (e: React.SyntheticEvent) => {
      pinPanelBankSection?.(panelBankSectionConfigRef);
      // this button always unpins a section
      trackPanelBankEvent({action: 'Unpin panel bank section'});
      e.stopPropagation();
    },
    [panelBankSectionConfigRef, pinPanelBankSection]
  );

  return (
    <div className={twMerge('flex h-[48px] items-center', 'px-12 py-4')}>
      {!isHiddenPanelsSection && (
        <Button
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
          aria-label="Drag section"
          className="cursor-grab active:cursor-grabbing"
          icon="drag-grip"
          size="small"
          variant="ghost"
        />
      )}
      <div className={twMerge('panel-bank__section-title', 'w-full')}>
        <span
          className="panel-bank__section-title-left"
          onClick={e => {
            // Important note: we do some css padding hacks to increase clickable area to toggle section right now.
            // user should be able to click on icon or title to toggle section
            toggleSectionIsOpen();
            e.stopPropagation();
          }}>
          <Button
            icon={sectionIsOpen ? 'chevron-down' : 'chevron-next'}
            variant="ghost"
            size="small"
            className="mr-8"
            aria-label={`${
              sectionIsOpen
                ? `Collapse ${sectionName} section`
                : `Expand ${sectionName} section`
            }`}
          />
          <PanelBankSectionTitle
            sectionName={sectionName}
            updateName={updateName}
            isSectionNameSearchMatch={isSectionNameSearchMatch ?? false}
            panelSearchMatchCount={panelSearchMatchCount}
            activePanelCount={activePanelCount}
            isSearching={isSearching}
            isEditingName={isEditingName}
            setIsEditingName={setIsEditingName}
            onRemovePinnedSection={isPinned ? onRemovePinnedSection : undefined}
          />
          {/* TODO - remove this when releasing. this is for dev debugging */}
          <div className="ml-8 font-semibold text-red-500">
            {panelBankSectionConfigRef.id}
          </div>
        </span>
        {!isHiddenPanelsSection && (
          <>
            <div
              className="panel-bank__section-title-right"
              style={sectionIsOpen ? {} : {visibility: 'hidden'}}>
              <PanelBankSectionPagination
                activePanelCount={activePanelCount}
                isSectionNameSearchMatch={isSectionNameSearchMatch ?? false}
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                panelSearchMatchCount={panelSearchMatchCount}
                sectionLayoutType={sectionLayoutType}
              />
              <SectionSettings
                isPinned={isPinned}
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                sectionComponentRef={sectionComponentRef}
                pinPanelBankSection={pinPanelBankSection}
              />
              <PanelBankSectionOverflowMenu
                isProjectReadOnly={isProjectReadOnly}
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                sectionComponentRef={sectionComponentRef}
                setIsEditingName={setIsEditingName}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
